import React, { createContext, useContext, useState } from 'react';
import { NotesService } from '@/services/NotesService';

interface NotesContextType {
  unreadCount: number;
  setUnreadCount: React.Dispatch<React.SetStateAction<number>>;
  fetchUnreadCount: () => Promise<void>;
}

const NotesContext = createContext<NotesContextType | undefined>(undefined);

export function NotesProvider({ children }: { children: React.ReactNode }) {
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchUnreadCount = async () => {
    // Get memberId at runtime, not during component initialization
    const memberRaw = localStorage.getItem('member');
    let memberId = '';
    try {
      memberId = memberRaw ? JSON.parse(memberRaw)?.id || '' : '';
    } catch {
      memberId = '';
    }
    
    // Don't fetch if memberId is empty - prevents /api/members//notes/unread-count error
    if (!memberId || memberId.trim() === '') {
      setUnreadCount(0);
      return;
    }
    
    try {
      // Create service directly only when needed with valid memberId
      const notesService = new NotesService(memberId);
      const count = await notesService.getFamilyUnreadCount();
      setUnreadCount(count || 0);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
      setUnreadCount(0);
    }
  };

  return (
    <NotesContext.Provider value={{ unreadCount, setUnreadCount, fetchUnreadCount }}>
      {children}
    </NotesContext.Provider>
  );
}

export function useNotesContext() {
  const ctx = useContext(NotesContext);
  if (!ctx) throw new Error('useNotesContext must be used within NotesProvider');
  return ctx;
}
